import React, { useState, useRef, useEffect } from 'react';
import { Plus } from 'lucide-react';
import UserPropertyFilterModule from 'features/activity/components/UserPropertyFilterModule';
import UserFilterChip from 'features/activity/components/UserFilterChip';
import UserWhoFilterChip from 'features/activity/components/UserWhoFilterChip';

const isUserProperty = (f) => (f.propertySource || f.category) === 'user';
const isGroupProperty = (f) => (f.propertySource || f.category) === 'group';
const isWhereStyleProperty = (f) => isUserProperty(f) || isGroupProperty(f);
const isWhoPropertyForCohortBuilder = (f) => (f.propertySource || f.category) === 'event';
const isWhoPropertyFull = (f) => (f.propertySource || f.category) === 'event';

const isLegacyTransactionWhoFilter = (f) => {
  const src = f.propertySource || f.category;
  if (src === 'transaction') return true;
  const field = f.field || f.property || '';
  return field.startsWith('transaction_count:') || field.startsWith('transaction_agg:');
};

/**
 * Users filter module: WHERE block (above) + WHO block (below).
 * - WHERE: User properties with WHERE/AND/OR
 * - WHO: Event counts with WHO/AND/OR, Did/Did not, numeric operators
 */
const UsersFilterModule = ({
  filters = [],
  onFiltersChange,
  teamId,
  cohortBuilder = false,
}) => {
  const isWhoProperty = cohortBuilder ? isWhoPropertyForCohortBuilder : isWhoPropertyFull;

  useEffect(() => {
    const next = (filters || []).filter((f) => !isLegacyTransactionWhoFilter(f));
    if (next.length !== (filters || []).length) {
      onFiltersChange(next);
    }
  }, [filters, onFiltersChange]);
  const [isAddPopupOpen, setIsAddPopupOpen] = useState(false);
  const [editingFilter, setEditingFilter] = useState(null);
  const addButtonRef = useRef(null);

  const whereFilters = (filters || []).filter(isWhereStyleProperty);
  const whoFilters = (filters || []).filter(isWhoProperty);

  const handleAddFilter = () => {
    setEditingFilter(null);
    setIsAddPopupOpen(true);
  };

  const handlePropertyAdd = (newFilter) => {
    const isWho = isWhoProperty(newFilter);
    const targetList = isWho ? whoFilters : whereFilters;
    const firstConnector = isWho ? 'WHO' : 'WHERE';

    const connector = targetList.length === 0
      ? firstConnector
      : (filters.some((f) => f.id === newFilter.id) ? (filters.find((f) => f.id === newFilter.id)?.connector || 'AND') : 'AND');

    const withConnector = { ...newFilter, connector };
    if (filters.some((f) => f.id === newFilter.id)) {
      onFiltersChange(filters.map((f) => (f.id === newFilter.id ? withConnector : f)));
    } else {
      onFiltersChange([...filters, withConnector]);
    }
    setIsAddPopupOpen(false);
    setEditingFilter(null);
  };

  const handleFilterUpdate = (filterId, updates) => {
    onFiltersChange(filters.map((f) => (f.id === filterId ? { ...f, ...updates } : f)));
  };

  const handleFilterRemove = (filterId) => {
    const remaining = filters.filter((f) => f.id !== filterId);
    const whereRemaining = remaining.filter(isWhereStyleProperty);
    const whoRemaining = remaining.filter(isWhoProperty);
    const withConnectors = remaining.map((f) => {
      const isWho = isWhoProperty(f);
      const list = isWho ? whoRemaining : whereRemaining;
      const idx = list.findIndex((x) => x.id === f.id);
      const connector = idx === 0 ? (isWho ? 'WHO' : 'WHERE') : (f.connector === 'OR' ? 'OR' : 'AND');
      return { ...f, connector };
    });
    onFiltersChange(withConnectors);
    if (editingFilter?.id === filterId) {
      setEditingFilter(null);
      setIsAddPopupOpen(false);
    }
  };

  const handleEditFilter = (filter) => {
    setEditingFilter(filter);
    setIsAddPopupOpen(true);
  };

  const hasFilters = filters.length > 0;

  return (
    <div className="bg-transparent border border-dotted border-line-strong rounded-lg overflow-hidden" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className="bg-transparent overflow-hidden">
        <div className="pl-4 pr-6 pt-2 pb-3 flex items-center justify-between border-b border-dotted border-line-strong">
          <h3 className="text-xs font-bold uppercase tracking-wide text-[color:var(--text-primary)]">
            FILTERS
          </h3>
        </div>
        <div className="p-3 sm:p-6">
          {/* WHERE block (above) - User + group properties (cohort: includes group membership) */}
          {whereFilters.length > 0 && (
            <div className="space-y-2 mb-4">
              {whereFilters.map((filter, index) => (
                <UserFilterChip
                  key={filter.id}
                  filter={{
                    ...filter,
                    propertySource: filter.propertySource || filter.category || 'user',
                    property: filter.property || filter.field,
                    propertyLabel: filter.propertyLabel,
                    propertyType: filter.propertyType,
                  }}
                  isFirst={index === 0}
                  teamId={teamId}
                  onEdit={() => handleEditFilter(filter)}
                  onUpdate={(updates) => handleFilterUpdate(filter.id, updates)}
                  onRemove={() => handleFilterRemove(filter.id)}
                />
              ))}
            </div>
          )}

          {/* WHO block (below) - event counts */}
          {whoFilters.length > 0 && (
            <div className="space-y-2 mb-4">
              {whoFilters.map((filter, index) => (
                <UserWhoFilterChip
                  key={filter.id}
                  filter={{
                    ...filter,
                    propertySource: filter.propertySource || filter.category || 'event',
                    property: filter.property || filter.field,
                    propertyLabel: filter.propertyLabel,
                    propertyType: filter.propertyType,
                    didOperator: filter.didOperator || 'did',
                  }}
                  isFirst={index === 0}
                  teamId={teamId}
                  onEdit={() => handleEditFilter(filter)}
                  onUpdate={(updates) => handleFilterUpdate(filter.id, updates)}
                  onRemove={() => handleFilterRemove(filter.id)}
                />
              ))}
            </div>
          )}

          <div>
            <button
              ref={addButtonRef}
              type="button"
              onClick={handleAddFilter}
              className="flex items-center gap-2 text-sm text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)] transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span>Add</span>
            </button>
          </div>
        </div>
      </div>

      {hasFilters && (
        <div className="px-6 pb-2">
          <button
            type="button"
            onClick={() => onFiltersChange([])}
            className="text-sm text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]"
          >
            Clear all
          </button>
        </div>
      )}

      {(isAddPopupOpen || editingFilter) && (
        <UserPropertyFilterModule
          isOpen={isAddPopupOpen || !!editingFilter}
          onClose={() => {
            setIsAddPopupOpen(false);
            setEditingFilter(null);
          }}
          triggerRef={addButtonRef}
          teamId={teamId}
          filters={filters}
          existingFilter={editingFilter}
          onAdd={handlePropertyAdd}
          cohortBuilder={cohortBuilder}
        />
      )}
    </div>
  );
};

export default UsersFilterModule;
